import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { FILETYPES, createId, createRandomNumber } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { AssystFeeSettings } from '@models/businessSettings/settings';
import { ActingForType, MatterFileType } from '@models/matter';
import {
	MatterMortgage,
	MatterMortgageInstructionSource,
	MatterMortgageType,
	MortgageStep,
	TransactionType,
} from '@models/mortgage';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterMortgagesRepoService } from '@repos/businesses/sales/mortgages.service';
import { BusinessState } from '@state/business';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import LogRocket from 'logrocket';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { SelectMortgageInstructionsComponent } from '../llc/components/select-mortgage-instructions/select-mortgage-instructions.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { TestHooks } from './add-mortgage.hooks';

@Component({
	selector: 'app-add-mortgage',
	templateUrl: './add-mortgage.component.html',
	styleUrls: ['./add-mortgage.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class AddMortgageComponent {
	testHooks = TestHooks;
	public selected: MatterMortgageInstructionSource;
	public types = MatterMortgageInstructionSource;
	public matterFileType = MatterFileType;
	public transactionTypes = TransactionType;
	public selectedTransactionType = TransactionType.ARRANGED;
	public ICON = ICON;
	public uploadFile = false;
	public fileUploadTypes = [FILETYPES.XML];
	public parsed = false;
	public saving = false;

	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(MatterMortgagesState.getAllMortgages)
	mortgages: MatterMortgage[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.actingFor) actingFor: ActingForType;
	@SelectSnapshot(SelectedMatter.agreementDate) agreementDate: string;
	@SelectSnapshot(BusinessState.getAssystFeeSettings)
	assystSettings: AssystFeeSettings;
	@Select(MatterMortgagesState.getVTBMortgage)
	vtbMortgage$: Observable<MatterMortgage>;

	canAddVtbMortgage$: Observable<boolean>;

	constructor(
		private dialogRef: MatDialogRef<AddMortgageComponent>,
		private navService: NavigationService,
		private matterMortgageRepo: MatterMortgagesRepoService,
		private dialog: MatDialog,
	) {
		this.canAddVtbMortgage$ = this.vtbMortgage$.pipe(
			map((vtbMortgage) => !vtbMortgage),
		);
	}

	close(): void {
		this.dialogRef.close();
	}

	async continue(): Promise<void> {
		switch (this.selected) {
			case this.types.FAX:
			case this.types.EMAIL:
			case this.types.ASSYST:
				return await this.save();
			case this.types.CLOSER_SYNC:
				break;
			case this.types.LLC:
				this.dialog.open(SelectMortgageInstructionsComponent, {
					height: '640px',
					maxHeight: '95vh',
					maxWidth: '95vw',
					width: '995px',
					panelClass: 'no-padding',
				});
				this.close();
				break;
		}
	}

	xmlSelected(file): void {
		const reader = new FileReader();
		reader.readAsText(file, 'UTF-8');
		reader.onerror = function (evt) {
			console.log('error reading file', evt);
		};
	}

	backToSelect(): void {
		this.uploadFile = false;
	}

	async save(): Promise<void> {
		this.saving = true;

		const instructionSource = this.selected;

		const priority =
			this.selectedTransactionType === TransactionType.BRIDGE
				? null
				: this.mortgages?.filter((x) => x.priority !== null)?.length + 1;

		const matterMortgage: Partial<MatterMortgage> = {
			id: createId(),
			numericId: createRandomNumber(),
			source: instructionSource,
			type: MatterMortgageType.MANUAL,
			transactionType: this.selectedTransactionType,
			priority,
			assystFeeSettings: this.assystSettings,
			step: MortgageStep.INTAKE,
		};

		if (this.fileType === MatterFileType.REFINANCE) {
			matterMortgage.actingForMortgagee =
				this.actingFor !== ActingForType.MORTGAGOR;
			matterMortgage.commitmentDate = this.agreementDate ?? '';
		}

		try {
			await this.matterMortgageRepo.Create(matterMortgage as MatterMortgage, {
				matterId: this.matterId,
			});
			this.navService.goToMatter_Mortgages_Mortgage_Intake(
				this.matterId,
				matterMortgage.id,
			);
			this.dialogRef.close();
		} catch (err) {
			LogRocket.log('Unable to create manual mortgage', err);
		} finally {
			this.saving = false;
		}
	}
}
